import React, { useEffect, useMemo } from 'react';
import { Modal, Alert, Button, FormRow } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { getSalesPipelines, syncProjectToOplaCrm } from 'redux/crm/projectSlice';
import { SELECT_STYLES } from 'helpers/formStyles';

const COLOR_MAP = {
  primary:   '#3b82f6',
  secondary: '#64748b',
  success:   '#22c55e',
  warning:   '#f59e0b',
  info:      '#06b6d4',
  danger:    '#ef4444',
};

const ChangeStatusProjectForm = ({ showModal, setshowModal, project }) => {
  const { control, handleSubmit, formState: { errors }, setValue } = useForm();
  const dispatch  = useDispatch();
  const pipelines = useSelector(s => s.crm_project?.salesPipelines || []);
  const loading   = useSelector(s => s.crm_project?.salesPipelinesLoading || false);

  useEffect(() => {
    if (showModal) dispatch(getSalesPipelines());
  }, [dispatch, showModal]);

  // Build react-select grouped options from group_name
  const groupedOptions = useMemo(() => {
    const groups = {};
    pipelines.forEach(p => {
      const group = p.group_name || 'Other';
      if (!groups[group]) groups[group] = [];
      groups[group].push(p);
    });
    return Object.entries(groups).map(([label, options]) => ({
      label,
      options: options.map(p => ({
        value: p,
        label: p.name,
      })),
    }));
  }, [pipelines]);

  const closeModal = () => setshowModal(false);

  const onSubmit = async (data) => {
    const pipeline = data.pipeline;
    try {
      await dispatch(syncProjectToOplaCrm({
        projectId: project.id,
        data: {
          // pipeline.name is mapped to Opla stage UUID via OPLA_STAGE_NAME_TO_PIPELINE on the server
          stage_name: pipeline.name,
        },
      })).unwrap();
      toast.success(`Status changed to "${pipeline.name}"`);
      closeModal();
    } catch (error) {
      toast.error(error?.message || 'Failed to change status.');
    }
  };

  const formatOptionLabel = ({ label, value: p }) => (
    <div className="tw-flex tw-items-center tw-gap-2">
      <span
        className="tw-w-2.5 tw-h-2.5 tw-rounded-xl tw-flex-shrink-0"
        style={{ backgroundColor: COLOR_MAP[p?.color] || '#94a3b8' }}
      />
      <span>{label}</span>
    </div>
  );

  const groupStyles = {
    ...SELECT_STYLES,
    groupHeading: (base) => ({
      ...base,
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6366f1',
      padding: '6px 12px 2px',
    }),
  };

  const footer = (
    <>
      <Button type="submit" size="sm" onClick={handleSubmit(onSubmit)}>Change</Button>
      <Button variant="danger" size="sm" onClick={closeModal}>Cancel</Button>
    </>
  );

  return (
    <Modal
      show={showModal}
      onHide={closeModal}
      title="Change status!"
      backdropClick={false}
      footer={footer}
    >
      <Alert type="warning" className="tw-mb-4">
        This function allows you to change the status of a project. Specify the desired status for project{' '}
        <strong>{project.name}</strong>.
      </Alert>

      <FormRow label="Status" required error={errors.pipeline?.message}>
        <Controller
          control={control}
          name="pipeline"
          rules={{ required: 'Status is required.' }}
          render={() => (
            <Select
              closeMenuOnSelect
              options={groupedOptions}
              placeholder={loading ? 'Loading...' : 'Select status...'}
              menuPosition="fixed"
              styles={groupStyles}
              isLoading={loading}
              formatOptionLabel={formatOptionLabel}
              onChange={opt => setValue('pipeline', opt.value, { shouldValidate: true })}
            />
          )}
        />
      </FormRow>
    </Modal>
  );
};

ChangeStatusProjectForm.propTypes = {
  showModal:    PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  project:      PropTypes.object.isRequired,
};

export default ChangeStatusProjectForm;
